import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Alert,
  Container,
  Row,
  Col,
  Card,
  Image,
  FormCheck,
  Tabs,
  Tab,
} from "react-bootstrap";
import { FiPlusCircle, FiEdit, FiTrash2 } from "react-icons/fi";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";

const API_URL = `${process.env.REACT_APP_APIURL}api/`;
const UPLOADS_URL = `${process.env.REACT_APP_APIURL}api/uploads/`;

const BannerTable = ({
  banners,
  isLoading,
  error,
  onEdit,
  onDelete,
  onToggleStatus,
  type,
}) => {
  if (isLoading)
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
      </div>
    );
  if (error) return <Alert variant="danger">{error.message}</Alert>;

  return (
    <Table striped bordered hover responsive className="align-middle mt-3">
      <thead>
        <tr>
          <th>#</th>
          <th>Image</th>
          <th>Title</th>
          <th>Status</th>
          <th className="text-center">Actions</th>
        </tr>
      </thead>
      <tbody>
        {banners?.length === 0 ? (
          <tr>
            <td colSpan="5" className="text-center text-muted">
              No {type} banners found.
            </td>
          </tr>
        ) : (
          banners?.map((banner, index) => (
            <tr key={banner._id}>
              <td>{index + 1}</td>
              <td>
                <Image
                  src={`${UPLOADS_URL}${banner.image}`}
                  style={{ width: "120px", height: "60px", objectFit: "cover" }}
                  rounded
                />
              </td>
              <td>
                {banner.title || <em className="text-muted">No Title</em>}
              </td>
              <td>
                <FormCheck
                  type="switch"
                  id={`status-${type}-${banner._id}`}
                  label={banner.isActive ? "Active" : "Inactive"}
                  checked={banner.isActive}
                  onChange={() => onToggleStatus(banner._id)}
                />
              </td>
              <td className="text-center">
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="me-2"
                  onClick={() => onEdit(banner)}
                >
                  <FiEdit />
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => onDelete(banner)}
                >
                  <FiTrash2 />
                </Button>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </Table>
  );
};

export default function BannerManagement() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("pc");
  const [showModal, setShowModal] = useState(false);
  const [currentBanner, setCurrentBanner] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const initialFormData = {
    title: "",
    description: "",
    link: "/shop",
    buttonText: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const pcBannersQuery = useQuery({
    queryKey: ["pcBanners"],
    queryFn: async () => (await axios.get(`${API_URL}banner`)).data.data,
  });

  const mobileBannersQuery = useQuery({
    queryKey: ["mobileBanners"],
    queryFn: async () => (await axios.get(`${API_URL}mobile-banner`)).data.data,
  });

  const mutationConfig = (successMessage, errorMessage, queryKey) => ({
    onSuccess: () => {
      toast.success(successMessage);
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      handleCloseModal();
    },
    onError: (err) => toast.error(err.response?.data?.message || errorMessage),
  });

  const createMutation = useMutation({
    mutationFn: (payload) =>
      axios.post(
        `${API_URL}${activeTab === "pc" ? "banner" : "mobile-banner"}`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      ),
    ...mutationConfig(
      "Banner created!",
      "Error creating banner.",
      activeTab === "pc" ? "pcBanners" : "mobileBanners"
    ),
  });

  const updateMutation = useMutation({
    mutationFn: (payload) =>
      axios.put(
        `${API_URL}${activeTab === "pc" ? "banner" : "mobile-banner"}/${
          currentBanner._id
        }`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      ),
    ...mutationConfig(
      "Banner updated!",
      "Error updating banner.",
      activeTab === "pc" ? "pcBanners" : "mobileBanners"
    ),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) =>
      axios.delete(
        `${API_URL}${activeTab === "pc" ? "banner" : "mobile-banner"}/${id}`
      ),
    ...mutationConfig(
      "Banner deleted!",
      "Error deleting banner.",
      activeTab === "pc" ? "pcBanners" : "mobileBanners"
    ),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (id) =>
      axios.patch(
        `${API_URL}${
          activeTab === "pc" ? "banner" : "mobile-banner"
        }/${id}/toggle-status`
      ),
    ...mutationConfig(
      "Status updated!",
      "Error updating status.",
      activeTab === "pc" ? "pcBanners" : "mobileBanners"
    ),
  });

  const handleShowModal = (banner = null) => {
    if (banner) {
      setIsEditing(true);
      setCurrentBanner(banner);
      setFormData({
        title: banner.title || "",
        description: banner.description || "",
        link: banner.link || "/shop",
        buttonText: banner.buttonText || "",
      });
      setImagePreview(banner.image ? `${UPLOADS_URL}${banner.image}` : null);
    } else {
      setIsEditing(false);
      setCurrentBanner(null);
      setFormData(initialFormData);
      setImagePreview(null);
      setSelectedFile(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = new FormData();
    payload.append("title", formData.title);
    payload.append("description", formData.description);
    payload.append("link", formData.link);
    payload.append("buttonText", formData.buttonText);
    if (selectedFile) payload.append("image", selectedFile);

    if (isEditing) {
      updateMutation.mutate(payload);
    } else {
      if (!selectedFile) {
        toast.error("An image is required.");
        return;
      }
      createMutation.mutate(payload);
    }
  };

  const handleDeleteClick = (banner) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Delete "${banner.title || "Untitled"}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) deleteMutation.mutate(banner._id);
    });
  };

  return (
    <>
      <Toaster position="top-right" />
      <Topbar />
      <Sidebar />
      <main id="main" className="main">
        <Container fluid>
          <Row className="mb-4 align-items-center pt-3">
            <Col>
              <h1>Manage Banners</h1>
            </Col>
            <Col xs="auto">
              <Button variant="primary" onClick={() => handleShowModal()}>
                <FiPlusCircle className="me-2" /> Add New{" "}
                {activeTab === "pc" ? "PC" : "Mobile"} Banner
              </Button>
            </Col>
          </Row>

          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            id="banner-tabs"
            className="mb-3"
          >
            <Tab eventKey="pc" title="PC Banners">
              <BannerTable
                banners={pcBannersQuery.data}
                isLoading={pcBannersQuery.isLoading}
                error={pcBannersQuery.error}
                onEdit={handleShowModal}
                onDelete={handleDeleteClick}
                onToggleStatus={(id) => toggleStatusMutation.mutate(id)}
                type="PC"
              />
            </Tab>
            <Tab eventKey="mobile" title="Mobile Banners">
              <BannerTable
                banners={mobileBannersQuery.data}
                isLoading={mobileBannersQuery.isLoading}
                error={mobileBannersQuery.error}
                onEdit={handleShowModal}
                onDelete={handleDeleteClick}
                onToggleStatus={(id) => toggleStatusMutation.mutate(id)}
                type="Mobile"
              />
            </Tab>
          </Tabs>
        </Container>
      </main>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing
              ? `Edit ${activeTab.toUpperCase()}`
              : `Add New ${activeTab.toUpperCase()}`}{" "}
            Banner
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Title (Optional)</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Link</Form.Label>
              <Form.Control
                type="text"
                name="link"
                value={formData.link}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Button Text (Optional)</Form.Label>
              <Form.Control
                type="text"
                name="buttonText"
                value={formData.buttonText}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Image</Form.Label>
              <Form.Control
                type="file"
                name="image"
                ref={fileInputRef}
                onChange={handleFileChange}
                required={!isEditing}
              />
              {(imagePreview || (isEditing && currentBanner?.image)) && (
                <Image
                  src={imagePreview || `${UPLOADS_URL}${currentBanner.image}`}
                  thumbnail
                  className="mt-2"
                  style={{ maxHeight: "150px" }}
                />
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {isEditing ? "Save Changes" : "Create Banner"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}
